import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { getCookie } from 'src/common/helpers'

const SESSION_SHOWN_KEY = 'inactiveLeadPopupShown'
const DISMISS_KEY = 'inactiveLeadPopupDismissed'
const CONSENT_DISMISS_KEY = 'tracking_consent_banner_dismissed'

/** Minimum time after load before PZN fetch (avoids competing with LCP/hydration). */
const MIN_FETCH_DELAY_MS = 8000
/** Idle callback cap — fetch even if the main thread stays busy. */
const IDLE_TIMEOUT_MS = 12000

const isDismissedPermanently = () => {
  if (typeof window === 'undefined') return true
  try {
    return localStorage.getItem(DISMISS_KEY) === 'true'
  } catch {
    return false
  }
}

const wasShownThisSession = () => {
  if (typeof window === 'undefined') return true
  try {
    return sessionStorage.getItem(SESSION_SHOWN_KEY) === 'true'
  } catch {
    return false
  }
}

const isConsentBannerDismissed = () => {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(CONSENT_DISMISS_KEY) === 'true'
  } catch {
    return false
  }
}

const hasValidPznPayload = (pznResponse) =>
  !!(
    pznResponse &&
    typeof pznResponse === 'object' &&
    Object.keys(pznResponse).length > 0 &&
    pznResponse.eligibility_indicator?.toLowerCase() === 'y'
  )

const scheduleDeferredTask = (callback, { minDelayMs, idleTimeoutMs }) => {
  if (typeof window === 'undefined') return () => {}

  const startedAt = Date.now()
  let cancelled = false
  let idleId
  let delayId

  const runWhenReady = () => {
    if (cancelled) return

    const elapsed = Date.now() - startedAt
    if (elapsed < minDelayMs) {
      delayId = window.setTimeout(runWhenReady, minDelayMs - elapsed)

      return
    }

    callback()
  }

  if ('requestIdleCallback' in window) {
    idleId = window.requestIdleCallback(runWhenReady, {
      timeout: idleTimeoutMs,
    })
  } else {
    delayId = window.setTimeout(runWhenReady, minDelayMs)
  }

  return () => {
    cancelled = true
    if (idleId != null && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(idleId)
    }

    if (delayId != null) window.clearTimeout(delayId)
  }
}

/**
 * Fetches PZN data and controls the inactive-lead welcome popup with deferred timing.
 */
export function useInactiveLeadPznPopup({
  enabled = false,
  insightsLoaded = false,
  routerAsPath = '/',
  sideBarOpen = false,
}) {
  const [showInactiveLeadPopup, setShowInactiveLeadPopup] = useState(false)
  const [isCandidateForWelcomePopup, setIsCandidateForWelcomePopup] =
    useState(false)
  const [pznData, setPznData] = useState()
  const fetchStarted = useRef(false)

  const getDisplayBlockers = useCallback(() => {
    const blockers = []
    if (!enabled) blockers.push('not-enabled')
    if (isDismissedPermanently()) blockers.push('dismissed-permanent')
    if (wasShownThisSession()) blockers.push('shown-session')
    if (sideBarOpen) blockers.push('sidebar-open')
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden'
    ) {
      blockers.push('document-hidden')
    }

    if (!isConsentBannerDismissed()) blockers.push('consent-visible')

    return blockers
  }, [enabled, sideBarOpen])

  useEffect(() => {
    if (!enabled || !insightsLoaded) return undefined
    if (fetchStarted.current) return undefined
    if (isDismissedPermanently() || wasShownThisSession()) {
      return undefined
    }

    fetchStarted.current = true

    const cancelSchedule = scheduleDeferredTask(
      () => {
        const fetchPznInformation = async () => {
          try {
            const browserId = getCookie('browser_id')
            const ehVisitId = getCookie('eh_visit_id')
            const ehSessionId = getCookie('eh_session_id')
            const ehPreviousId = getCookie('eh_previous_visit_id')
            const tealiumId = typeof utag !== 'undefined' ? utag?.id : undefined
            const tealiumSessionId =
              typeof utag !== 'undefined'
                ? utag?.data?.tealium_session_id
                : undefined

            const { data } = await axios.post('/api/perzonalizate/', {
              visitId: ehVisitId || '',
              browserId: browserId || '',
              pageId: routerAsPath,
              tealium_session_id: tealiumSessionId || '',
              tealium_id: tealiumId || '',
              launch_darkly: '',
              previous_visit_id: ehPreviousId || '',
              session_id: ehSessionId || '',
              email: '',
              phone: '',
              profile_id: '',
            })

            const pznResponse = data?.response
            setPznData(pznResponse)
            const isEligible = hasValidPznPayload(pznResponse)
            setIsCandidateForWelcomePopup(isEligible)
          } catch {
            /* ignore */
          }
        }

        fetchPznInformation()
      },
      { minDelayMs: MIN_FETCH_DELAY_MS, idleTimeoutMs: IDLE_TIMEOUT_MS }
    )

    return cancelSchedule
  }, [enabled, insightsLoaded, routerAsPath])

  const tryOpenPopup = useCallback(() => {
    if (showInactiveLeadPopup) return false

    if (getDisplayBlockers().length > 0) {
      return false
    }

    try {
      sessionStorage.setItem(SESSION_SHOWN_KEY, 'true')
    } catch {
      /* ignore */
    }

    setShowInactiveLeadPopup(true)

    return true
  }, [getDisplayBlockers, showInactiveLeadPopup])

  useEffect(() => {
    if (!isCandidateForWelcomePopup || !pznData) return undefined

    if (tryOpenPopup()) return undefined

    const blockers = getDisplayBlockers()
    if (!blockers.includes('consent-visible')) return undefined

    const consentPoll = window.setInterval(() => {
      if (isConsentBannerDismissed()) {
        window.clearInterval(consentPoll)
        tryOpenPopup()
      }
    }, 500)

    const consentPollTimeout = window.setTimeout(() => {
      window.clearInterval(consentPoll)
    }, 120000)

    return () => {
      window.clearInterval(consentPoll)
      window.clearTimeout(consentPollTimeout)
    }
  }, [
    isCandidateForWelcomePopup,
    pznData,
    getDisplayBlockers,
    tryOpenPopup,
    sideBarOpen,
  ])

  const closeInactiveLeadPopup = useCallback(() => {
    setShowInactiveLeadPopup(false)
    try {
      localStorage.setItem(DISMISS_KEY, 'true')
      sessionStorage.setItem(SESSION_SHOWN_KEY, 'true')
    } catch {
      /* ignore */
    }
  }, [])

  return {
    shouldShowInactiveLeadWelcomePopup: showInactiveLeadPopup,
    isCandidateForWelcomePopup,
    pznData,
    closeInactiveLeadPopup,
  }
}
